<template>
  <div class="relative flex flex-col" :class="colSpan">
    <p v-if="field?.display" class="w-full sm:text-md">
      {{ field.display }} {{ !isRequired ? $t('optional') : '' }}
    </p>
    <p v-if="instructions">{{ instructions }}</p>
    <ul
      class="flex flex-1 flex-col flex-wrap space-y-4 py-5"
      :class="{
        'nav:flex-row nav:space-x-10 nav:space-y-0': field?.config?.inline,
      }"
    >
      <li v-for="(option, index) in computedOptions" :key="index">
        <label class="checkbox-container" :for="`${idPrefix}${option.value}`">
          <span class="sm:text-md">
            {{ option.label }}
          </span>

          <Field
            :id="`${idPrefix}${option.value}`"
            :value="useValue ? option.value : option.label"
            :name="name || handle || field.handle"
            :label="label || option.label || field.label || field.display"
            :disabled="disabled"
            :rules="rulesAsString"
            :type="type"
            :model-value="modelValue"
            @update:model-value="$emit('update:model-value', $event)"
          />

          <span class="checkbox-checkmark" />
        </label>
      </li>
    </ul>

    <Transition name="fade">
      <p
        v-if="error"
        class="bottom-0 top-16 text-sm text-red has-bg-img:absolute has-bg-img:bottom-2.5 has-bg-img:line-clamp-1 has-bg-img:px-5 xl:pt-1"
      >
        {{ $t('this_is_required') }}
      </p>
    </Transition>
  </div>
</template>

<script setup>
import { Field } from 'vee-validate'

defineEmits(['update:model-value'])

const props = defineProps({
  options: {
    type: [Array, Object],
    default: () => [],
  },

  handle: {
    type: String,
    default: null,
  },

  type: {
    type: String,
    default: 'checkbox',
  },

  modelValue: {
    type: [String, Number, Boolean],
    default: null,
  },

  field: {
    type: Object,
    default: null,
  },

  width: {
    type: [Number, String],
    default: null,
  },

  rules: {
    type: [Array, String],
    default: '',
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  label: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: null,
  },

  instructions: {
    type: String,
    default: null,
  },

  error: {
    type: String,
    default: null,
  },
  idPrefix: {
    type: String,
    default: '',
  },
  useValue: {
    type: Boolean,
    default: false,
  },
})

const rulesAsString = computed(() => {
  if (Array.isArray(props.rules)) {
    return props.rules.join('|')
  }

  return props.rules
})

const isRequired = computed(() => {
  return rulesAsString.value.includes('required')
})

const colSpan = computed(() => {
  const width = parseInt(props.width) || props.field?.width || 100

  switch (width) {
    case 25:
      return 'md:col-span-6 lg:col-span-3 col-span-12'
    case 33:
      return 'md:col-span-6 lg:col-span-4 col-span-12'
    case 50:
      return 'md:col-span-6 lg:col-span-6 col-span-12'
    case 66:
      return 'md:col-span-6 lg:col-span-8 col-span-12'
    default:
      return 'md:col-span-6 lg:col-span-12 col-span-12'
  }
})

const computedOptions = computed(() => {
  if (Array.isArray(props.options)) {
    return props.options.map((option) => {
      if (typeof option === 'string') {
        return {
          label: option,
          value: option,
        }
      }

      return option
    })
  }

  return Object.keys(props.options).map((key) => ({
    label: props.options[key],
    value: key,
  }))
})
</script>

<style scoped>
.checkbox-container {
  @apply block relative pl-8 cursor-pointer select-none;
}

.checkbox-container input {
  @apply absolute opacity-0 cursor-pointer;
}

.checkbox-checkmark {
  @apply absolute mt-1 inset-x-0 top-0 bg-white rounded-sm border-2 border-blue-800 transition-all duration-100 ease-in-out;
  width: 1.375rem;
  height: 1.375rem;
}

.checkbox-container input:focus-visible ~ .checkbox-checkmark {
  @apply ring;
}

.checkbox-checkmark:after {
  @apply absolute opacity-0 transition-all duration-150 ease-in-out;

  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12.023' height='9.688' viewBox='0 0 12.023 9.688'%3E%3Cpath id='Path_556' data-name='Path 556' d='M-23312.98-6465.871l2.719,3.312,3.762-4.221,2.723-3.055' transform='translate(23314.389 6471.247)' fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2'/%3E%3C/svg%3E%0A");
  top: -0.23rem;
  left: 0.22rem;
}

.checkbox-container input:checked ~ .checkbox-checkmark:after {
  @apply opacity-100;
}

.checkbox-container input:checked ~ .checkbox-checkmark {
  @apply bg-blue-800;
}
</style>
